import React, { Fragment, useEffect, useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import { LeftMenuItem } from '../shared/LeftMenuItem';
import { GroupMenuItem } from './GroupMenuItem';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import {
    USER_ROLES,
    PRACTICE_OWNER_TYPES,
    PRACTICE_STATUS_ENUM,
} from '../../../../utils/constants';
import { PRACTICE_OWNERSHIP_TYPES } from '../../../../utils/constants/PracticeOwnerType.enum';
import { ASSOCIATE_MEMBER_RIGHTS_ENUM } from '../../../../utils/associateMembersConstants';

export interface MyGroupsMenuItemProps {
    summary: any;
    limits: any;
    headingClassName?: string;
    headingTo?: string;
}

const isOwnerOrAssociateAndHasAccessRight = (
    summary: any,
    accessRight: number
) => {
    if (
        summary.roleId === USER_ROLES.PRACTICE_OWNER &&
        summary.practiceOwnerType === PRACTICE_OWNER_TYPES.ASPIRING &&
        accessRight === ASSOCIATE_MEMBER_RIGHTS_ENUM.POC
    ) {
        return false;
    }
    return (
        summary.roleId === USER_ROLES.PRACTICE_OWNER ||
        (summary.roleId === USER_ROLES.USER &&
            summary.practiceOwnerType === PRACTICE_OWNER_TYPES.FORMER) ||
        (summary.roleId === USER_ROLES.PRACTICE_SECONDARY_USER &&
            summary.associateMember &&
            summary.associateMember.some((x: any) =>
                x.practices.some((p: any) =>
                    p.acceptedAccessRights.includes(accessRight)
                )
            ))
    );
};

const doesUserHasAccessRight = (summary: any) =>
    summary.roleId === USER_ROLES.USER && !summary.isReferral;

export const MyGroupsMenuItem = (props: MyGroupsMenuItemProps) => {
    const { summary, limits, headingClassName, headingTo } = props;
    const axios = useStateSelector((state) => state.core.axios);
    const [uniqueGroups, setUniqueGroups] = useState<any[]>([]);

    useEffect(() => {
        let cancelled = false;

        const build = async () => {
            let isCorporate = false;
            let corpGroup: any = undefined;

            if (
                summary.roleId === USER_ROLES.PRACTICE_OWNER &&
                summary.practiceOwnerType ===
                    PRACTICE_OWNERSHIP_TYPES.CORPORATE &&
                !!summary.corporateAffiliation
            ) {
                const response = await axios.get(
                    `api/CorporateGroups/${summary.corporateAffiliation.id}`
                );
                isCorporate = true;
                corpGroup = response && response.data ? response.data : undefined;
            } else {
                isCorporate = !!summary.corporateAffiliation;
                corpGroup = undefined;
            }

            const practices = summary.practices || [];
            const isAnyPracticeApproved =
                practices.filter(
                    (x: any) => x.status === PRACTICE_STATUS_ENUM.Approved
                ).length > 0;
            const isAnyActivePracticePending =
                practices.filter(
                    (x: any) =>
                        x.status === PRACTICE_STATUS_ENUM.Pending &&
                        x.practiceOwnershipType !==
                            PRACTICE_OWNERSHIP_TYPES.FORMER
                ).length > 0;

            let groups: any[] = [];
            if (summary.groups) {
                groups = Array.from(
                    new Set(summary.groups.map((g: any) => g.groupId))
                ).map((id) => {
                    const grp = summary.groups.find(
                        (g: any) => g.groupId === id
                    );
                    return {
                        groupId: id,
                        groupName: grp.groupName,
                        notificationFlagPrefix: `${NotificationFlagsCategories.PmgCode}.${grp.groupId}`,
                    };
                });
            }

            if (
                isOwnerOrAssociateAndHasAccessRight(
                    summary,
                    ASSOCIATE_MEMBER_RIGHTS_ENUM.KnowledgeBase
                ) ||
                doesUserHasAccessRight(summary)
            ) {
                groups.unshift({
                    groupName: 'Practice Management Knowledge Library',
                    to: '/knowledge-library',
                    isCustom: true,
                    notificationFlagPrefix:
                        NotificationFlagsCategories.KnowledgeLibraryCode,
                });
            }

            if (
                isOwnerOrAssociateAndHasAccessRight(
                    summary,
                    ASSOCIATE_MEMBER_RIGHTS_ENUM.POC
                ) &&
                limits.currentSubscriptionLimitsState.doesPocAvailable &&
                (isAnyPracticeApproved || isAnyActivePracticePending) &&
                (!isCorporate || !!corpGroup?.pocId)
            ) {
                const poc =
                    isCorporate && corpGroup?.pocId
                        ? {
                              groupName: `${corpGroup?.name} Communty`,
                              to: `/cpoc/${corpGroup?.id}`,
                              isCustom: true,
                              notificationFlagPrefix:
                                  NotificationFlagsCategories.PocCode,
                          }
                        : {
                              groupName: 'The IPOC',
                              to: '/ipoc',
                              isCustom: true,
                              notificationFlagPrefix:
                                  NotificationFlagsCategories.PocCode,
                          };
                groups.unshift(poc);
            }

            if (!cancelled) {
                setUniqueGroups(groups);
            }
        };

        build();
        return () => {
            cancelled = true;
        };
    }, [summary, limits, axios]);

    const hasApprovedPractice =
        (summary.practices || []).filter(
            (x: any) => x.status === PRACTICE_STATUS_ENUM.Approved
        ).length > 0;

    return (
        <Fragment>
            <LeftMenuItem
                icon="pe-7s-users"
                label="My Groups & Resources"
                to={headingTo}
                className={headingClassName}
                trackingAction="My Groups & Resources"
            />
            {uniqueGroups.map((group, index) => (
                <GroupMenuItem
                    key={index}
                    group={group}
                    hasApprovedPractice={hasApprovedPractice}
                    isReferral={summary.isReferral}
                />
            ))}
        </Fragment>
    );
};
